import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  CheckCircle2,
  AlertCircle,
  Music,
  FileText,
  Radio,
  Disc3,
} from "lucide-react";

interface ValidationResult {
  platform: string;
  icon: React.ReactNode;
  status: "valid" | "warning" | "error";
  message: string;
}

interface MetadataValidatorProps {
  metadata: Record<string, any>;
}

export function MetadataValidator({ metadata }: MetadataValidatorProps) {
  const validationResults: ValidationResult[] = [
    {
      platform: "GRAMMY Awards",
      icon: <Music className="w-5 h-5" />,
      status: metadata.isrc ? "valid" : "error",
      message: metadata.isrc ? "ISRC code detected" : "ISRC code required",
    },
    {
      platform: "Luminate",
      icon: <FileText className="w-5 h-5" />,
      status: metadata.title && metadata.artist ? "valid" : "warning",
      message:
        metadata.title && metadata.artist
          ? "Essential metadata complete"
          : "Missing artist or title",
    },
    {
      platform: "Apple Music",
      icon: <Radio className="w-5 h-5" />,
      status:
        metadata.title && metadata.artist && metadata.album ? "valid" : "warning",
      message:
        metadata.title && metadata.artist && metadata.album
          ? "All fields complete"
          : "Missing album information",
    },
    {
      platform: "Discogs",
      icon: <Disc3 className="w-5 h-5" />,
      status: metadata.genre ? "valid" : "warning",
      message: metadata.genre ? "Genre specified" : "Genre recommended",
    },
  ];

  return (
    <Card className="w-full max-w-2xl p-6">
      <h2 className="text-2xl font-bold mb-6">Platform Compatibility</h2>
      <div className="space-y-3">
        {validationResults.map((result, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
          >
            <div className="flex items-center gap-3 flex-1">
              <div className="text-muted-foreground">{result.icon}</div>
              <div>
                <p className="font-semibold">{result.platform}</p>
                <p className="text-sm text-muted-foreground">{result.message}</p>
              </div>
            </div>
            <div>
              {result.status === "valid" && (
                <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Valid
                </Badge>
              )}
              {result.status === "warning" && (
                <Badge
                  variant="outline"
                  className="bg-amber-50 text-amber-700 border-amber-200"
                >
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Partial
                </Badge>
              )}
              {result.status === "error" && (
                <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                  <AlertCircle className="w-3 h-3 mr-1" />
                  Missing
                </Badge>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
